import { addBreadcrumb, captureException } from '@sentry/vue'
import { getHeaders, processUrl } from '@/lib/net'
import { Vertical } from '@/types/verticals'

interface AppStartRecord {
  ts: string
  synced: boolean
}

const buildKey = (accountId: string, app: Vertical) =>
  `onx.app-start.${accountId}.${app}`

const readRecord = (key: string): AppStartRecord | null => {
  try {
    const raw = localStorage.getItem(key)
    return raw ? (JSON.parse(raw) as AppStartRecord) : null
  } catch (e) {
    return null
  }
}

const writeRecord = (key: string, record: AppStartRecord) => {
  try {
    localStorage.setItem(key, JSON.stringify(record))
  } catch (e) {
    // ignore storage write failures (quota exceeded, disabled storage, etc.)
  }
}

/**
 * Posts the app-start payload and returns the response status, or null if
 * the request failed outright (e.g. network error).
 */
const postAppStart = async (app: Vertical, firstStartTs: string) => {
  try {
    const response = await fetch(processUrl('/v1/app-starts'), {
      method: 'POST',
      headers: await getHeaders(),
      body: JSON.stringify({ app, first_start_ts: firstStartTs }),
    })
    return response.status
  } catch (e) {
    return null
  }
}

class AppStartService {
  /**
   * Records a first-start timestamp for this (accountId, app) pair on first
   * call, then POSTs it to Growth Infra until the server confirms receipt.
   * Never marks a record synced before the POST resolves; leaves it unsynced
   * on any outcome other than a 200 or 400 response (e.g. 401, 5xx, network
   * errors) so the next load retries.
   */
  async sync(accountId: string) {
    try {
      const app = Vertical.Fish
      const key = buildKey(accountId, app)
      let record = readRecord(key)

      if (!record) {
        record = { ts: new Date().toISOString(), synced: false }
        writeRecord(key, record)
      }

      if (record.synced) {
        return
      }

      const status = await postAppStart(app, record.ts)

      if (status === null) {
        addBreadcrumb({
          category: 'app-start',
          level: 'info',
          message: 'app-start POST network error, will retry next load',
        })
        return
      }

      const isPermanentOutcome = status === 200 || status === 400

      if (isPermanentOutcome) {
        writeRecord(key, { ...record, synced: true })
      } else {
        addBreadcrumb({
          category: 'app-start',
          level: 'info',
          message: `app-start POST returned ${status}, will retry next load`,
        })
      }
    } catch (e) {
      captureException(e)
    }
  }
}

export default new AppStartService()
